<script setup>
import { computed } from 'vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';

defineProps({
  keepAlive: {
    type: Boolean,
    default: true,
  },
});

const route = useRoute();
const { t } = useI18n();
const { accountScopedRoute } = useAccount();

const shouldShowSettingsBack = computed(() => route.name !== 'settings_home');
</script>

<template>
  <div
    class="settings-wrapper flex flex-col w-full h-full m-0 p-6 sm:py-8 lg:px-16 overflow-auto bg-n-background font-inter"
  >
    <div
      class="tic-settings-content flex flex-col items-start w-full max-w-6xl mx-auto"
    >
      <RouterLink
        v-if="shouldShowSettingsBack"
        :to="accountScopedRoute('settings_home')"
        class="tic-settings-back"
      >
        <span class="i-lucide-arrow-left size-4" />
        <span>{{ t('SETTINGS.PLATFORM_HOME.BACK_TO_SETTINGS') }}</span>
      </RouterLink>
      <div
        class="tic-settings-route-frame"
        :class="{ 'is-home': route.name === 'settings_home' }"
      >
        <router-view v-slot="{ Component }">
          <keep-alive v-if="keepAlive">
            <component :is="Component" />
          </keep-alive>
          <component :is="Component" v-else />
        </router-view>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.tic-settings-content {
  min-height: 100%;
}

.tic-settings-route-frame {
  width: 100%;
  max-width: 56rem;
  margin-inline: auto;

  &.is-home {
    max-width: none;
  }
}

.tic-settings-back {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  padding: 0.62rem 0.9rem;
  border: 1px solid rgba(148, 234, 255, 0.16);
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.05);
  color: rgba(248, 250, 252, 0.94);
  font-size: 0.82rem;
  font-weight: 800;
  text-decoration: none;

  &:hover {
    border-color: rgba(38, 217, 247, 0.38);
    background: rgba(38, 217, 247, 0.1);
  }
}

:global(body:not(.dark)) .tic-settings-back {
  background: rgba(255, 255, 255, 0.88);
  border-color: rgba(15, 23, 42, 0.08);
  color: #111827;
  box-shadow: 0 12px 26px rgba(31, 38, 135, 0.08);
}
</style>
